import React, { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { invalidateAgentQueries } from "@/lib/invalidateAgentQueries";
import type { QueryClient } from "@tanstack/react-query";
import type { ExecutionPlan, OrderConfirmation } from "@/hooks/voice-agent/types";

export function usePlanManagement(
  siteId: string | undefined,
  queryClient: QueryClient,
  setPendingExecutionPlan: React.Dispatch<React.SetStateAction<ExecutionPlan | null>>,
  setConfirmations: React.Dispatch<React.SetStateAction<OrderConfirmation[]>>,
) {
  // Confirm execution plan
  const confirmPlan = useCallback(async (planId: string) => {
    logger.info("useVoiceAgent", "Confirming plan", { planId });

    try {
      const { data, error } = await supabase.functions.invoke("voice-agent", {
        body: {
          confirmPlanId: planId,
          siteId,
        },
      });

      if (error) throw error;

      if (data?.success) {
        toast.success("Action confirmée", { description: data.message || "L'action a été exécutée avec succès.",
        });

        setPendingExecutionPlan(null);

        if (data.confirmation) {
          setConfirmations(prev => [...prev, {
            type: data.confirmation.type,
            data: data.confirmation.data,
            ts: new Date().toISOString(),
          }]);
        }

        // Invalidate relevant queries
        invalidateAgentQueries(queryClient);
      } else {
        throw new Error(data?.error || "Confirmation failed");
      }

    } catch (error) {
      logger.error("useVoiceAgent", "Plan confirmation error", error);
      toast.error("Erreur de confirmation", { description: error instanceof Error ? error.message : "La confirmation a échoué"});
      setPendingExecutionPlan(null);
    }
  }, [siteId, queryClient, setPendingExecutionPlan, setConfirmations]);

  // Cancel execution plan
  const cancelPlan = useCallback(async (planId: string) => {
    logger.info("useVoiceAgent", "Cancelling plan", { planId });

    try {
      await supabase
        .from("execution_plans")
        .update({ status: "cancelled" })
        .eq("id", planId);

      setPendingExecutionPlan(null);
      toast.success("Action annulée", { description: "Le plan a été annulé.",
      });

    } catch (error) {
      logger.error("useVoiceAgent", "Plan cancellation error", error);
    }
  }, [setPendingExecutionPlan]);

  // Modify execution plan
  const modifyPlan = useCallback(async (planId: string, modifiedInputs: Record<string, any>) => {
    logger.info("useVoiceAgent", "Modifying plan", { planId, modifiedInputs });

    try {
      const { error } = await supabase
        .from("execution_plans")
        .update({
          inputs_normalized: modifiedInputs,
          status: "pending" // Keep as pending for re-confirmation
        })
        .eq("id", planId);

      if (error) throw error;

      // Update local state with modified plan
      setPendingExecutionPlan(prev => prev ? {
        ...prev,
        inputs_normalized: modifiedInputs,
      } : null);

      toast.success("Plan modifié", { description: "Vérifiez et confirmez le plan mis à jour.",
      });

    } catch (error) {
      logger.error("useVoiceAgent", "Plan modification error", error);
      toast.error("Erreur de modification", { description: error instanceof Error ? error.message : "La modification a échoué"});
    }
  }, [setPendingExecutionPlan]);

  return { confirmPlan, cancelPlan, modifyPlan };
}
